// Who a claim says has died, and which covered lives that makes deceased.
//
// There is no death marker on a dependant record — neither the policy service nor
// the claims service carries one. A death claim IS the record of the death, so a
// member's deceased state has to be read back off the claims raised on the policy.
//
// No optional chaining / ES6+ syntax the project's Babel config rejects.

import { getFullName } from './personDetails.es6'

// Benefit names vary by partner and product: DEATH BENEFIT, Funeral, Last_Expense,
// Funeral Natural Death, Accidental Death.
const DEATH_BENEFIT = /death|funeral|last[\s_]*expense/i

// A discarded claim should never have been logged, so it is disregarded outright.
// A rejected one is reported rather than acted on: it does not mark the member
// deceased, but it is worth surfacing that a death was claimed for and turned
// down, so an assessor is not left wondering why nothing is shown.
const DISCARDED = 'Discarded'
const REJECTED = 'Rejected'

function additionalDetails(claim) {
  const details = claim && claim.claim_details
  return (details && details.additional_details) || {}
}

/** What the claim records as the cause of death, or null. */
export function causeOfDeathFromClaim(claim) {
  return additionalDetails(claim).cause_of_death || null
}

/** The more specific cause, where the partner captures one. */
export function exactCauseOfDeathFromClaim(claim) {
  return additionalDetails(claim).cause_of_death_detail || null
}

/** Who the claim is for, as recorded on the claim itself. */
export function deceasedNameFromClaim(claim) {
  const details = claim && claim.claim_details
  return (details && details.dependant_fullname) || null
}

/**
 * Does this claim have the shape of a death claim, whatever became of it?
 *
 * A recorded cause of death settles it. Otherwise fall back to the benefit
 * names, since a partner may not capture a cause at all.
 */
export function looksLikeDeathClaim(claim) {
  if (!claim || claim.status === DISCARDED) {
    return false
  }
  if (causeOfDeathFromClaim(claim)) {
    return true
  }
  const benefits = claim.benefits || []
  for (let i = 0; i < benefits.length; i++) {
    if (benefits[i] && DEATH_BENEFIT.test(benefits[i].type || '')) {
      return true
    }
  }
  return false
}

/** A death claim that stands — i.e. one that has not been turned down. */
export function isDeathClaim(claim) {
  return looksLikeDeathClaim(claim) && claim.status !== REJECTED
}

function normalisedName(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Does this claim name this dependant?
 *
 * Guid first. The name fallback exists because `dependant_guid` is not on every
 * claim payload, and without it a member with a death claim would keep showing
 * as active.
 */
export function claimIsForDependant(claim, dependant) {
  if (!claim || !dependant) {
    return false
  }
  if (claim.dependant_guid && dependant.guid) {
    return claim.dependant_guid === dependant.guid
  }
  const claimed = normalisedName(deceasedNameFromClaim(claim))
  return !!claimed && claimed === normalisedName(getFullName(dependant))
}

/** Has a death claim that stands been raised for this member? */
export function isDependantDeceased(dependant, claims) {
  const list = claims || []
  for (let i = 0; i < list.length; i++) {
    if (isDeathClaim(list[i]) && claimIsForDependant(list[i], dependant)) {
      return true
    }
  }
  return false
}

/**
 * The turned-down death claim for this member, or null.
 *
 * Only consulted when the member is not deceased — a rejected claim alongside an
 * approved one says nothing worth reporting.
 */
export function rejectedDeathClaimFor(dependant, claims) {
  const list = claims || []
  for (let i = 0; i < list.length; i++) {
    const claim = list[i]
    if (
      claim.status === REJECTED &&
      looksLikeDeathClaim(claim) &&
      claimIsForDependant(claim, dependant)
    ) {
      return claim
    }
  }
  return null
}
